/**
 * A BOQ laid out as a document, for printing or saving as a PDF through the
 * browser's print dialog (#512). No PDF library: the page is the document,
 * and the toolbar above it is left out of the printout.
 *
 * Reachable by anyone the backend lets read the BOQ (the homeowner, the
 * project's designer, a hired contractor), from the BOQ page and the
 * homeowner's BOQ tab. Amounts arrive in paise and are shown in rupees.
 */

import { Printer } from "lucide-react";
import { useParams } from "react-router-dom";
import { formatUom } from "@/features/catalog/utils/uom";
import { useBOQDetails } from "../../hooks/use-boq";
import { useProject } from "../../hooks/use-projects";
import {
	type BOQDetails,
	type BOQEntry,
	formatCurrency,
	getStatusLabel,
} from "../../services/boq/boq-api.service";
import {
	formatGstAmount,
	formatGstIncluded,
	formatGstRate,
	GST_INCLUDED_HINT,
	summariseGstIncluded,
} from "../../utils/boq-gst";

/** On every BOQ: how GST appears in it (see the BOQ product guide). */
export const GST_NOTE =
	"All prices include GST. The GST incl. column shows the part of each catalogue price that is already inside it; it is not added on top, and the totals are unchanged. A dash means the price is not the current catalogue price (typed in or quoted), so its GST is not known.";

const DRAFT_NOTE = "This BOQ is still being prepared and may change.";

/**
 * The sheet is paper: dark ink on white whatever the app's theme, on screen and
 * in print, so its colours are fixed literals rather than the theme's tokens
 * (which turn light-on-dark in dark mode and would print pale text). They are
 * declared once, on the first rule, and used through the custom properties.
 */
const styles = `
.boq-print { --ink: #111827; --muted: #4b5563; --faint: #6b7280; --label: #374151; --rule-strong: #9ca3af; --rule: #e5e7eb; --box: #d1d5db; --paper: #ffffff; --desk: #f3f4f6; --shadow: rgba(0, 0, 0, 0.12); }
.boq-print { background: var(--desk); min-height: 100vh; color: var(--ink); font-family: var(--font-body, system-ui, sans-serif); }
.boq-print__toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; max-width: 900px; margin: 0 auto; padding: 16px; }
.boq-print__hint { margin: 0; font-size: 13px; color: var(--muted); }
.boq-print__button { display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 16px; border: 0; border-radius: 7px; background: var(--ink); color: var(--paper); font-size: 14px; font-weight: 600; cursor: pointer; }
.boq-print__sheet { max-width: 900px; margin: 0 auto 32px; padding: 32px; background: var(--paper); box-shadow: 0 1px 3px var(--shadow); }
.boq-print__brand { margin: 0 0 16px; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--faint); }
.boq-print__project { margin: 0; font-size: 14px; color: var(--muted); }
.boq-print__title { margin: 4px 0 8px; font-size: 24px; font-weight: 600; }
.boq-print__meta { margin: 0; font-size: 13px; color: var(--muted); }
.boq-print__notice { margin: 16px 0 0; padding: 8px 12px; border: 1px solid var(--box); font-size: 13px; }
.boq-print__section { margin-top: 28px; break-inside: auto; }
.boq-print__section h2 { margin: 0 0 8px; font-size: 16px; font-weight: 600; }
.boq-print__scroll { overflow-x: auto; }
.boq-print table { width: 100%; border-collapse: collapse; font-size: 12px; }
.boq-print th { text-align: left; font-weight: 600; color: var(--label); border-bottom: 1px solid var(--rule-strong); padding: 6px 8px; white-space: nowrap; }
.boq-print td { border-bottom: 1px solid var(--rule); padding: 6px 8px; vertical-align: top; }
.boq-print tr { break-inside: avoid; }
.boq-print .num { text-align: right; white-space: nowrap; }
.boq-print__sku { display: block; color: var(--faint); font-size: 11px; }
.boq-print__subtotal { display: flex; justify-content: space-between; gap: 12px; padding: 6px 8px; font-size: 12px; font-weight: 600; }
.boq-print__inline { display: none; }
.boq-print__total { display: flex; justify-content: space-between; margin-top: 28px; padding-top: 12px; border-top: 2px solid var(--ink); font-size: 16px; font-weight: 700; }
.boq-print__gstsub { display: flex; justify-content: space-between; gap: 12px; padding: 0 8px 6px; font-size: 12px; color: var(--muted); }
.boq-print__gst { margin: 12px 0 0; font-size: 12px; color: var(--muted); }
.boq-print__state { max-width: 900px; margin: 0 auto; padding: 32px 16px; font-size: 14px; }
@media screen and (max-width: 600px) {
  .boq-print__sheet { padding: 16px; }
  .boq-print .col-wide { display: none; }
  .boq-print__inline { display: inline; }
  .boq-print__room { display: block; color: var(--faint); font-size: 11px; }
  .boq-print th, .boq-print td { padding: 6px 4px; }
}
@media print {
  @page { margin: 14mm; }
  .boq-print { background: var(--paper); min-height: 0; }
  .boq-print__toolbar { display: none; }
  .boq-print__sheet { max-width: none; margin: 0; padding: 0; box-shadow: none; }
  .boq-print__scroll { overflow: visible; }
}
`;

const dateFormat = new Intl.DateTimeFormat("en-IN", {
	day: "numeric",
	month: "short",
	year: "numeric",
});

function EntryRow({
	entry,
	currencyCode,
}: {
	entry: BOQEntry;
	currencyCode: string;
}) {
	const room = entry.roomCustomName || entry.roomName || "";
	const unit = entry.uom ? formatUom(entry.uom) : "";
	return (
		<tr>
			<td className="num col-wide">{entry.lineNo}</td>
			<td>
				{entry.product?.name ?? "Unnamed product"}
				{entry.variant?.sku && (
					<span className="boq-print__sku">{entry.variant.sku}</span>
				)}
				{entry.description && (
					<span className="boq-print__sku">{entry.description}</span>
				)}
				{room && (
					<span className="boq-print__inline boq-print__room">{room}</span>
				)}
			</td>
			<td className="col-wide">{room}</td>
			<td className="num">
				{entry.quantity}
				{unit && <span className="boq-print__inline"> {unit}</span>}
			</td>
			<td className="col-wide">{unit}</td>
			<td className="num">{formatCurrency(entry.unitPrice, currencyCode)}</td>
			<td className="num">{formatCurrency(entry.lineTotal, currencyCode)}</td>
			<td className="num">
				{formatGstAmount(entry.gstAmount, currencyCode)}
				<span className="boq-print__sku">{formatGstRate(entry.gstRateBp)}</span>
			</td>
		</tr>
	);
}

export function BoqPrintDocument({
	details,
	projectTitle,
}: {
	details: BOQDetails;
	projectTitle: string | null;
}) {
	const { boq, sections } = details;
	const currency = boq.currencyCode || "INR";
	const draft = boq.status === "draft" || boq.status === "change_requested";

	return (
		<article className="boq-print__sheet">
			<p className="boq-print__brand">Finishd · Bill of quantities</p>
			{projectTitle && <p className="boq-print__project">{projectTitle}</p>}
			<h1 className="boq-print__title">
				{boq.title || `BOQ version ${boq.versionNumber}`}
			</h1>
			<p className="boq-print__meta">
				Version {boq.versionNumber} · {getStatusLabel(boq.status)} · Updated{" "}
				{dateFormat.format(new Date(boq.updatedAt))}
			</p>
			{draft && <p className="boq-print__notice">{DRAFT_NOTE}</p>}

			{sections.map((section) => (
				<section key={section.id} className="boq-print__section">
					<h2>{section.sectionName}</h2>
					<div className="boq-print__scroll">
						<table>
							<thead>
								<tr>
									<th className="num col-wide">#</th>
									<th>Item</th>
									<th className="col-wide">Room</th>
									<th className="num">Qty</th>
									<th className="col-wide">Unit</th>
									<th className="num">Rate</th>
									<th className="num">Amount</th>
									<th className="num" title={GST_INCLUDED_HINT}>
										GST incl.
									</th>
								</tr>
							</thead>
							<tbody>
								{section.entries.map((entry) => (
									<EntryRow
										key={entry.id}
										entry={entry}
										currencyCode={currency}
									/>
								))}
							</tbody>
						</table>
					</div>
					<div className="boq-print__subtotal">
						<span>{section.sectionName} total</span>
						<span>{formatCurrency(section.sectionTotal, currency)}</span>
					</div>
					<div className="boq-print__gstsub">
						<span>GST included in {section.sectionName}</span>
						<span>
							{formatGstIncluded(
								summariseGstIncluded(section.entries),
								currency,
							)}
						</span>
					</div>
				</section>
			))}

			<div className="boq-print__total">
				<span>Grand total</span>
				<span>{formatCurrency(boq.grandTotal, currency)}</span>
			</div>
			<div className="boq-print__gstsub">
				<span>GST included</span>
				<span>
					{formatGstIncluded(
						summariseGstIncluded(
							sections.flatMap((section) => section.entries),
						),
						currency,
					)}
				</span>
			</div>
			<p className="boq-print__gst">{GST_NOTE}</p>
		</article>
	);
}

export function BoqPrintPage() {
	const { boqId = "" } = useParams<{ boqId: string }>();
	const { data: details, isLoading, isError } = useBOQDetails(boqId, !!boqId);
	const { data: project } = useProject(details?.boq.projectId ?? "");

	return (
		<div className="boq-print">
			<style>{styles}</style>
			{isLoading ? (
				<p className="boq-print__state">Loading BOQ…</p>
			) : isError || !details ? (
				<p className="boq-print__state">This BOQ could not be loaded.</p>
			) : (
				<>
					<div className="boq-print__toolbar">
						<p className="boq-print__hint">
							To save a PDF, choose "Save as PDF" as the printer.
						</p>
						<button
							type="button"
							className="boq-print__button"
							onClick={() => window.print()}
						>
							<Printer size={16} aria-hidden />
							Print / Save as PDF
						</button>
					</div>
					<BoqPrintDocument
						details={details}
						projectTitle={project?.title ?? null}
					/>
				</>
			)}
		</div>
	);
}
